import React, { Fragment, useState, memo, useEffect } from 'react';
import PropTypes from 'prop-types';

import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import format from 'date-fns/format';
import addYears from 'date-fns/addYears';
import {
  Check,
  History,
  Image as ImageIcon,
  Info,
  Plus,
  Search,
  Send,
  Sparkles,
  Video,
  Zap,
} from 'lucide-react';

import {
  Button,
  Form,
  FormGroup,
  Checkbox,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';

import {
  currencySymbol,
  moreExamplesNeededToPitch,
  pluralOrSingular,
} from 'helpers/HelperFunctions';
import { featuredRolesMatchJobRole } from 'helpers/RoleMatching';
import { load as loadSubscriptionPortalUrl } from 'redux/modules/subscriptions';

import DatePicker from 'components/Shared/DatePicker';
import SvgIcon from 'components/SvgIcon/SvgIcon';
import FormSelect from 'components/FormSelect/FormSelect';
import ImproveRanking from 'components/BriefDetailsFreelancer/CreativePitch/ImproveRanking/ImproveRanking';
import RoleEligibilityBlock from 'components/BriefDetailsFreelancer/CreativePitch/RoleEligibilityBlock/RoleEligibilityBlock';
import Panel from 'components/Panel/Panel';
import styles from './CreativePitch.module.scss';
import CreativePitchUserCard from 'components/BriefDetailsFreelancer/CreativePitch/CreativePitchUserCard';
import { MINIMUM_INITIAL_PITCH_AMOUNT } from 'helpers/globalVariables';
import Modal from 'components/Modal/Modal';
import Loader from 'components/Loader/Loader';
import { trackEvent } from 'helpers/Analytics';
import { timeUntil } from 'helpers/PitchLimitHelper';
import { isLinkedInLandingJob } from 'helpers/linkedinLandingJob';
import { FREE_USER_JOB_PITCH_LIMIT } from 'constants/constants';
import { isPaygCreditsPricingEnabled } from 'helpers/ApplicationCreditsHelper';
import { APPLICATION_TYPE_INTEREST } from 'helpers/showInterest';
const EASY_APPLY_TOOLTIP = 'Apply quickly with Twine';
const NO_COVER_LETTER_TOOLTIP =
  'Searching jobs take your portfolio and CV/resume instead of a written application.';

const getPitchPlaceholder = (briefRole) => {
  const roleName = (briefRole || '').toLowerCase().trim();
  if (roleName) {
    return `Hi, I'm a ${roleName} … For a project like this I'd…`;
  }
  return `Hi, I'm a freelance creative. For a project like this I'd…`;
};

const CreativePitch = ({
  availabilityToggle,

  briefCurrency,
  briefId,
  defaultInterestMessage,
  briefRole,
  briefPaid,

  briefRemote,
  briefLocation,
  briefNoPitchLimits,
  creativeJob,

  creditsRoles,

  introsLeft,
  introsLoading,

  pitchText,
  pitchAmount,
  submittingPitch,
  totalPitches,
  pitchError,
  applicationCreditsRemaining = 0,
  willUseCredits = false,
  pitchAgain = false,
  showInterest = false,

  onChangeAvailability,
  onChangeAvailabilityToggle,
  onChangePitchAmount,
  onChangePitchText,
  onGeneratePitch,
  onImportLastPitch,
  onSubmitPitch,

  loadingGeneratedPitch,

  locationCheckbox,
  onLocationCheckboxClick,
  shareResumeCheckbox,
  onShareResumeCheckboxClick,
  onConfirmationCheckboxClick,
  authUserWasInvited,
  onAttachUpload,

  jobRole,
  suggestedRoles,
  loadingSuggestedRoles,

  user,
}) => {
  const dispatch = useDispatch();
  const TODAY = new Date();
  const [date, setDate] = useState(new Date(user.available_at || TODAY));
  const [approxProjectFee, setApproxProjectFee] = useState(0);
  const [feeInputVisibility, setFeeInputVisibility] = useState(false);
  const [showProModal, setShowProModal] = useState(false);
  const [interestNoteOpen, setInterestNoteOpen] = useState(pitchText !== '');
  const userJoined = new Date(user.joined);
  const nextRenewalDate = new Date(
    TODAY.getFullYear(),
    TODAY.getMonth(),
    userJoined.getDate()
  );
  if (nextRenewalDate <= TODAY)
    nextRenewalDate.setMonth(nextRenewalDate.getMonth() + 1);
  const pitchLimitRenewal = timeUntil(TODAY, nextRenewalDate);

  const dailyResetIn = timeUntil(TODAY);

  const { subscriptionUrl } = useSelector((state) => state.subscriptions);

  useEffect(() => {
    onChangePitchAmount(0);
    if (user && user.subscribed) {
      dispatch(loadSubscriptionPortalUrl());
    }
  }, []);

  const hasBio = user.bio !== '';

  const lacksRelevantProjects =
    creativeJob && moreExamplesNeededToPitch(briefRole, creditsRoles);

  // const shouldShowLacksProjects = lacksRelevantProjects[1] > 0;
  const shouldShowLacksProjects = false;

  // Role eligibility: block when the job has a role, the user was not invited,
  // and none of their featured roles match the job role or its related roles.
  // const isRoleIneligible =
  //   jobRole &&
  //   !authUserWasInvited &&
  //   !loadingSuggestedRoles &&
  //   !featuredRolesMatchJobRole(
  //     user.featured_roles || [],
  //     jobRole,
  //     suggestedRoles || []
  //   );
  // Disabled role eligibility check for now
  const isRoleIneligible = false;

  // if lacksRelevantProjects returns false, set to empty Array of length 2
  const [required, remaining] = lacksRelevantProjects || Array(2);

  // Only used by discountUpsell (currently disabled)
  // const hasCreativeRole = () => {
  //   const creativeRoles = [
  //     'Content Creator',
  //     'Actor',
  //     'Social Media Freelancer',
  //     'Social Media Manager',
  //     'Content Producer',
  //     'UGC Creator',
  //     'Digital Marketer',
  //     'Actress',
  //     'Presenter',
  //     'Community Manager',
  //     'Influencer Marketing Manager',
  //   ];
  //   return (
  //     user &&
  //     user.featured_roles &&
  //     user.featured_roles.length > 0 &&
  //     user.featured_roles.some((role) => creativeRoles.includes(role))
  //   );
  // };

  const getSubscriptionUrl = () => {
    if (subscriptionUrl) {
      return subscriptionUrl;
    }

    return `https://billing.stripe.com/p/login/4gw5kmbsI0Cb1q0cMM?prefilled_email=${encodeURIComponent(
      user.email
    )}`;
  };

  // Disabled — community contribution upsell
  const discountUpsell = null;
  // const discountUpsell = !user.pro && hasCreativeRole() && (
  //   <div className={styles.discountUpsell}>
  //     Want a free month of Twine Pro? Contribute to the Twine community and earn
  //     rewards.{' '}
  //     <a
  //       target="_blank"
  //       rel="noreferrer"
  //       href="https://twinehq.notion.site/Twine-Community-Contributions-2093bde70b248047823fd85f0318a8ee?source=copy_link/"
  //     >
  //       Learn how it works.
  //     </a>
  //   </div>
  // );

  const noCreditsBalanceMessage =
    isPaygCreditsPricingEnabled(user) &&
    user.has_purchased_credits &&
    applicationCreditsRemaining <= 0 ? (
      <p
        style={{
          textAlign: 'center',
          fontFamily: 'Plus Jakarta Sans',
          fontSize: '14px',
          color: '#c0392b',
        }}
      >
        You have no application credits remaining
      </p>
    ) : null;

  const lackBioMessage = (
    <Fragment>
      <p>
        <strong>You need to add a bio</strong>
      </p>
      <p>Fill out a short bio on your profile.</p>
    </Fragment>
  );
  const lackProjectsMessage = (
    <Fragment>
      <p>
        <strong>You need a medium profile score</strong> and{' '}
        {briefRole.toLowerCase()} jobs require <strong>{required}</strong>{' '}
        relevant profile {pluralOrSingular(required, 'example')}.
      </p>
      <p>
        Add <strong>{remaining}</strong> more{' '}
        {pluralOrSingular(remaining, 'example')} to contact this client.
      </p>
    </Fragment>
  );

  const activateAccount = (
    <Panel
      className="box-shadow"
      style={{
        padding: '30px 20px',
      }}
    >
      <p className="text-center">
        <SvgIcon icon="email" color="#0777DA" size="48" />
      </p>
      <p
        style={{
          textAlign: 'center',
          fontFamily: 'Plus Jakarta Sans',
          fontWeight: '700',
          fontSize: '20px',
        }}
      >
        <strong>Confirm your email</strong>
      </p>
      <p
        style={{
          textAlign: 'center',
          fontFamily: 'Plus Jakarta Sans',
          fontSize: '16px',
          maxWidth: '350px',
          margin: 'auto',
        }}
      >
        Twine has sent you an activation email.
        <br />
        Open your email and <b>click the link</b> to activate your account
        before you can apply.
        <br />
      </p>
      <p
        style={{
          textAlign: 'center',
          fontFamily: 'Plus Jakarta Sans',
          fontSize: '14px',
          color: '#666',
          marginTop: '20px',
        }}
      >
        Once activated,{' '}
        <a href="#" onClick={() => window.location.reload()}>
          reload the page
        </a>
        .
      </p>
    </Panel>
  );

  const maximumPitchLimit = (
    <Panel
      className="box-shadow"
      style={{
        padding: '30px 20px',
      }}
    >
      <p className="text-center">
        <SvgIcon icon="clock" color="#0777DA" size="48" />
      </p>
      <p
        style={{
          textAlign: 'center',
          fontFamily: 'Plus Jakarta Sans',
          fontWeight: '700',
          fontSize: '20px',
        }}
      >
        <strong>This is a Pro and Premium job</strong>
      </p>
      <p
        style={{
          textAlign: 'center',
          fontFamily: 'Plus Jakarta Sans',
          fontSize: '16px',
        }}
      >
        Once a job becomes Popular, only Pro or Premium members can apply.
        <br />
        This job is now Popular.
      </p>
      <p
        style={{
          textAlign: 'center',
          fontFamily: 'Plus Jakarta Sans',
          fontSize: '16px',
        }}
      >
        Still want to apply?
      </p>
      <p
        style={{
          textAlign: 'center',
          fontFamily: 'Plus Jakarta Sans',
          fontSize: '16px',
        }}
      >
        <Button bsStyle="primary" componentClass={Link} to="/pricing/creative">
          Upgrade now
        </Button>{' '}
        <Button bsStyle="default" componentClass={Link} to="/pricing/creative">
          Learn more
        </Button>
      </p>
      {noCreditsBalanceMessage}
      {discountUpsell}
    </Panel>
  );

  const noMorePitches = (
    <Panel
      className="box-shadow"
      style={{
        padding: '30px 20px',
      }}
    >
      <p className="text-center">
        <SvgIcon icon="clock" color="#0777DA" size="48" />
      </p>
      <p
        style={{
          textAlign: 'center',
          fontFamily: 'Plus Jakarta Sans',
          fontWeight: '700',
          fontSize: '20px',
        }}
      >
        <strong>Your applications limit reached</strong>
      </p>
      <p
        style={{
          textAlign: 'center',
          fontFamily: 'Plus Jakarta Sans',
          fontSize: '16px',
        }}
      >
        {user.pro
          ? `You've used your applications for this month. Next reset in ${pitchLimitRenewal}.`
          : `You've used today's application. Next one resets in ${dailyResetIn}.`}
        <br />
        {!user.pro ? (
          <>
            <a href="/pricing/creative" target="_blank" rel="noreferrer">
              Upgrade your account
            </a>{' '}
            for more applications
          </>
        ) : (
          !user.premium && (
            <>
              <a
                href={
                  getSubscriptionUrl()
                    ? getSubscriptionUrl()
                    : '/pricing/creative'
                }
                target="_blank"
                rel="noreferrer"
              >
                Upgrade to Premium
              </a>{' '}
              for no applications limits
            </>
          )
        )}
      </p>
      <p
        style={{
          textAlign: 'center',
          fontFamily: 'Plus Jakarta Sans',
          fontSize: '16px',
        }}
      >
        Still want to apply?{' '}
      </p>
      {noCreditsBalanceMessage}
      <p
        style={{
          textAlign: 'center',
          fontFamily: 'Plus Jakarta Sans',
          fontSize: '16px',
        }}
      >
        {!user.pro ? (
          <>
            <Link to="/pricing/creative">
              <Button bsStyle="primary">Upgrade your account</Button>
            </Link>
          </>
        ) : (
          !user.premium && (
            <>
              <Button
                href={
                  getSubscriptionUrl()
                    ? getSubscriptionUrl()
                    : '/pricing/creative'
                }
                bsStyle="primary"
              >
                Upgrade to Twine Premium
              </Button>
            </>
          )
        )}
      </p>
      {discountUpsell}
    </Panel>
  );

  const pitchAmountOptions = [
    {
      label: 'Need to discuss',
      value: 0,
    },
    {
      label:
        currencySymbol(briefCurrency, false) +
        MINIMUM_INITIAL_PITCH_AMOUNT.toString(),
      value: MINIMUM_INITIAL_PITCH_AMOUNT,
    },
    { label: currencySymbol(briefCurrency, false) + '100', value: 100 },
    { label: currencySymbol(briefCurrency, false) + '250', value: 250 },
    { label: currencySymbol(briefCurrency, false) + '500', value: 500 },
    { label: currencySymbol(briefCurrency, false) + '1000', value: 1000 },
    { label: currencySymbol(briefCurrency, false) + '5000', value: 5000 },
    { label: currencySymbol(briefCurrency, false) + '10000', value: 10000 },
    { label: 'Other amount', value: null },
  ];

  const handleApproxPitchFee = (approxFee) => {
    if (approxFee === 'Other amount') {
      setFeeInputVisibility(true);
    } else {
      setFeeInputVisibility(false);
      onChangePitchAmount(approxFee);
      setApproxProjectFee(approxFee);
    }
  };

  // Returns null if the user can pitch, or { element, paywallReason } describing
  // the blocking panel to show and the analytics reason (if any).
  const getPitchBlocker = () => {
    if (briefNoPitchLimits) return null;
    if (authUserWasInvited) return null;
    if (user.premium) return null;
    if (user.pro && user.messages_count > 0) return null;

    if (!user.email_verified)
      return { element: activateAccount, paywallReason: null };

    if (user.messages_count === 0 && applicationCreditsRemaining <= 0) {
      const reason =
        user.pro && !user.premium
          ? 'pro_user_pitch_limit_reached'
          : !user.pro
          ? 'free_user_pitch_limit_reached'
          : null;
      return { element: noMorePitches, paywallReason: reason };
    }

    if (!hasBio)
      return {
        element: (
          <ImproveRanking
            requiredRank="medium"
            message={lackBioMessage}
            profileLink={user.links.main_relative}
          />
        ),
        paywallReason: null,
      };

    if (shouldShowLacksProjects)
      return {
        element: (
          <ImproveRanking
            message={lackProjectsMessage}
            profileLink={user.links.main_relative}
          />
        ),
        paywallReason: null,
      };

    if (isLinkedInLandingJob()) return null;

    if (
      totalPitches > FREE_USER_JOB_PITCH_LIMIT &&
      applicationCreditsRemaining <= 0
    )
      return {
        element: maximumPitchLimit,
        paywallReason: 'free_user_job_pitch_limit_reached',
      };

    return null;
  };

  const pitchBlocker = getPitchBlocker();
  const canPitch = pitchBlocker === null;
  const paywallReason = pitchBlocker?.paywallReason ?? null;

  // True only when the LinkedIn bypass was the specific reason the popular-job
  // paywall was skipped — i.e. the user would otherwise have been blocked by
  // totalPitches > FREE_USER_JOB_PITCH_LIMIT but the cookie granted access.
  const linkedInBypassed =
    canPitch &&
    isLinkedInLandingJob() &&
    totalPitches > FREE_USER_JOB_PITCH_LIMIT;

  useEffect(() => {
    if (paywallReason && briefId && user?.id) {
      trackEvent('pro-paywall-shown', {
        userId: user.id,
        jobId: briefId,
        reason: paywallReason,
      });
    }

    if (linkedInBypassed && briefId && user?.id) {
      trackEvent('linkedin-landing-job-apply-eligible', {
        jobId: briefId,
        userId: user.id,
      });
    }

    if (briefId && user?.id) {
      trackEvent('freelancer-job-viewed', {
        briefId,
        role: briefRole,
        can_apply: canPitch,
        application_type: showInterest ? APPLICATION_TYPE_INTEREST : 'standard',
      });
    }
  }, [briefId, user?.id]);

  return (
    <div id="pitch-section">
      {/* Role eligibility block — shown before other gating panels */}
      {isRoleIneligible && (
        <RoleEligibilityBlock
          jobRole={jobRole}
          portfolioLink={user.links.main_relative}
        />
      )}

      {/* Loading state while suggested roles or allowance data are fetched */}
      {(loadingSuggestedRoles || introsLoading) && !authUserWasInvited && (
        <Loader />
      )}

      {/* Freelancer Validation Rules — only show when role is eligible and allowance is loaded */}
      {!isRoleIneligible &&
        !loadingSuggestedRoles &&
        !introsLoading &&
        pitchBlocker?.element}

      {/* Freelancer Pitch Components */}
      {canPitch &&
        !isRoleIneligible &&
        !loadingSuggestedRoles &&
        !introsLoading && (
          <div className={`${styles.container} box-shadow`}>
            {showInterest ? (
              <div
                className={`${styles.pitchTitleContainer} ${styles.interestIntro}`}
              >
                <div className={styles.titleRow}>
                  <h3 className={styles.title}>Show interest</h3>
                  <OverlayTrigger
                    placement="top"
                    trigger={['hover', 'focus', 'click']}
                    rootClose
                    container={
                      typeof document !== 'undefined'
                        ? document.body
                        : undefined
                    }
                    overlay={
                      <Tooltip id="pitch-no-cover-letter-tooltip">
                        {NO_COVER_LETTER_TOOLTIP}
                      </Tooltip>
                    }
                  >
                    <button
                      type="button"
                      className={styles.noCoverLetterLabel}
                      aria-label={`No cover letter. ${NO_COVER_LETTER_TOOLTIP}`}
                    >
                      <Check
                        className={styles.easyApplyIcon}
                        size={13}
                        strokeWidth={2.2}
                        aria-hidden="true"
                      />
                      No cover letter
                    </button>
                  </OverlayTrigger>
                </div>
                <p className={styles.guidanceLine}>
                  This client is looking at experts before deciding to hire.
                  Show interest and they get your portfolio to look through,
                  plus your resume if you share it.
                </p>
                <div className={styles.interestNote} role="note">
                  <Search
                    size={16}
                    aria-hidden="true"
                    className={styles.quotaIcon}
                  />
                  <span>
                    <strong>Searching jobs get fewer replies.</strong> The
                    client may only contact the experts they shortlist, so a
                    reply isn’t guaranteed.
                  </span>
                </div>
              </div>
            ) : (
              <div className={styles.pitchTitleContainer}>
                <div className={styles.titleRow}>
                  <h3 className={styles.title}>Apply to this job</h3>
                  <OverlayTrigger
                    placement="top"
                    trigger={['hover', 'focus', 'click']}
                    rootClose
                    container={
                      typeof document !== 'undefined'
                        ? document.body
                        : undefined
                    }
                    overlay={
                      <Tooltip id="pitch-easy-apply-tooltip">
                        {EASY_APPLY_TOOLTIP}
                      </Tooltip>
                    }
                  >
                    <button
                      type="button"
                      className={styles.easyApplyLabel}
                      aria-label={`Easy apply. ${EASY_APPLY_TOOLTIP}`}
                    >
                      <Zap
                        className={styles.easyApplyIcon}
                        size={12}
                        strokeWidth={2.4}
                        aria-hidden="true"
                      />
                      Easy apply
                    </button>
                  </OverlayTrigger>
                </div>
                <p className={styles.guidanceLine}>
                  Say what you’d do for this project and link to relevant work.
                  Short and specific wins.
                </p>
              </div>
            )}

            {showInterest ? (
              interestNoteOpen ? (
                <div className={styles.introField}>
                  <label
                    htmlFor="pitchIntroduction"
                    className={styles.fieldLabel}
                  >
                    Note to the client{' '}
                    <span className={styles.optionalLabel}>(optional)</span>
                  </label>
                  <textarea
                    id="pitchIntroduction"
                    className={`${styles.pitchTextarea} ${
                      styles.interestTextarea
                    } ${
                      pitchError && pitchError.pitchText ? styles.error : ''
                    }`}
                    disabled={submittingPitch}
                    maxLength="10000"
                    onChange={(event) => onChangePitchText(event.target.value)}
                    placeholder="I’ve done similar work before. Happy to share more if it helps."
                    rows="3"
                    value={pitchText}
                  />
                  <p className={styles.fieldHelper}>
                    One line on why this job suits you is plenty. Leave it empty
                    and we’ll send: “{defaultInterestMessage}”
                  </p>
                  {pitchError && pitchError.pitchText && (
                    <div className={styles.errorMsg}>
                      {pitchError.pitchText}
                    </div>
                  )}
                </div>
              ) : (
                <div className={styles.introField}>
                  <button
                    type="button"
                    className={styles.addNoteLink}
                    disabled={submittingPitch}
                    onClick={() => {
                      setInterestNoteOpen(true);
                      trackEvent('show-interest-note-opened', { briefId });
                    }}
                  >
                    <Plus size={16} strokeWidth={2.2} aria-hidden="true" />
                    Add a short note (optional)
                  </button>
                  <p className={styles.fieldHelper}>
                    Skip it and we’ll send: “{defaultInterestMessage}”
                  </p>
                </div>
              )
            ) : loadingGeneratedPitch ? (
              <div className={styles.loadingPitchContainer}>
                <h3 className={styles.loadingPitchTitle}>
                  Generating your application
                </h3>
                <p className={styles.loadingPitchText}>
                  Hang tight whilst AI generates your application
                </p>
                <Loader loading />
              </div>
            ) : (
              <div className={styles.introField}>
                <label
                  htmlFor="pitchIntroduction"
                  className={styles.fieldLabel}
                >
                  Your introduction
                </label>
                <textarea
                  id="pitchIntroduction"
                  className={`${styles.pitchTextarea} ${
                    pitchError && pitchError.pitchText ? styles.error : ''
                  }`}
                  disabled={submittingPitch}
                  maxLength="10000"
                  onChange={(event) => onChangePitchText(event.target.value)}
                  placeholder={getPitchPlaceholder(briefRole)}
                  rows="5"
                  value={pitchText}
                />
                {pitchError && pitchError.pitchText && (
                  <div className={styles.errorMsg}>{pitchError.pitchText}</div>
                )}
              </div>
            )}
            {showInterest && <hr className={styles.interestDivider} />}

            {!showInterest && (
              <div className={styles.pitchButtonsContainer}>
                <div className={styles.pitchButtonRow}>
                  <span className={styles.pitchButtonRowLabel}>Attach</span>
                  <div className={styles.pitchButtonGroup}>
                    <button
                      type="button"
                      title="Attach an example from your portfolio"
                      className={styles.secondaryButton}
                      onClick={() => onAttachUpload()}
                      disabled={submittingPitch || loadingGeneratedPitch}
                    >
                      <ImageIcon
                        className={styles.secondaryButtonIcon}
                        size={16}
                        strokeWidth={1.8}
                        aria-hidden="true"
                      />
                      Add portfolio example
                    </button>
                    <a
                      href="https://www2.twine.net/BoomPitch"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-disabled={submittingPitch || loadingGeneratedPitch}
                      className={styles.secondaryButton}
                      onClick={(event) => {
                        if (submittingPitch || loadingGeneratedPitch) {
                          event.preventDefault();
                        }
                      }}
                    >
                      <Video
                        className={styles.secondaryButtonIcon}
                        size={16}
                        strokeWidth={1.8}
                        aria-hidden="true"
                      />
                      Record a video
                    </a>
                  </div>
                </div>
                <div className={styles.pitchButtonRow}>
                  <span className={styles.pitchButtonRowLabel}>
                    Draft faster
                  </span>
                  <div className={styles.pitchButtonGroup}>
                    <button
                      type="button"
                      title="Import your most recent application"
                      className={styles.secondaryButton}
                      onClick={() => onImportLastPitch()}
                      disabled={submittingPitch || loadingGeneratedPitch}
                    >
                      <History
                        className={styles.secondaryButtonIcon}
                        size={16}
                        strokeWidth={1.8}
                        aria-hidden="true"
                      />
                      Use last application
                    </button>
                    <button
                      type="button"
                      className={styles.secondaryButton}
                      onClick={() => {
                        trackEvent('user-clicked-ai-pitch');
                        user.pro ? onGeneratePitch() : setShowProModal(true);
                      }}
                      disabled={submittingPitch || loadingGeneratedPitch}
                    >
                      <Sparkles
                        className={styles.secondaryButtonIcon}
                        size={16}
                        strokeWidth={1.8}
                        aria-hidden="true"
                      />
                      Write this for me
                    </button>
                  </div>
                </div>
              </div>
            )}

            {briefPaid && (
              <Fragment>
                {!showInterest && (
                  <div className={styles.fieldGrid}>
                    <div className={styles.fieldCell}>
                      <label
                        htmlFor="availabilityInput"
                        className={styles.fieldLabel}
                      >
                        Availability
                      </label>
                      <FormSelect
                        id="availabilityInput"
                        className={styles.fieldSelect}
                        disabled={submittingPitch}
                        onChange={onChangeAvailabilityToggle}
                        options={[
                          { label: 'Immediately', value: 0 },
                          { label: 'Starting from...', value: 1 },
                        ]}
                        value={availabilityToggle}
                      />
                      {availabilityToggle === 1 && (
                        <div className={styles.datePickerContainer}>
                          <DatePicker
                            dateFormat="do MMMM"
                            className={`form-control ${styles.datePicker}`}
                            minDate={TODAY}
                            maxDate={addYears(TODAY, 1)}
                            placeholderText="Select availability"
                            onChange={(date) => {
                              setDate(date);
                              onChangeAvailability(
                                format(date, 'yyyy-MM-dd HH:mm:ss')
                              );
                            }}
                            selected={date}
                          />
                        </div>
                      )}
                      {pitchError && pitchError.pitchAvailability && (
                        <div className={styles.errorMsg}>
                          {pitchError.pitchAvailability}
                        </div>
                      )}
                    </div>

                    <div className={styles.fieldCell}>
                      <label
                        htmlFor="projectFeeInput"
                        className={styles.fieldLabel}
                      >
                        Project fee
                      </label>
                      <FormSelect
                        id="projectFeeInput"
                        className={styles.fieldSelect}
                        disabled={submittingPitch}
                        onChange={(e) => handleApproxPitchFee(e)}
                        options={pitchAmountOptions}
                        value={
                          feeInputVisibility ? 'Other amount' : approxProjectFee
                        }
                      />
                      {feeInputVisibility === true && (
                        <input
                          className={`form-control ${styles.otherFeeInput} ${
                            pitchError && pitchError.pitchAmount
                              ? styles.error
                              : ''
                          }`}
                          disabled={submittingPitch}
                          onChange={(event) =>
                            onChangePitchAmount(event.target.value)
                          }
                          placeholder={`${currencySymbol(
                            briefCurrency,
                            false
                          )}500`}
                          type="number"
                          value={pitchAmount}
                        />
                      )}
                      <p className={styles.fieldHelper}>
                        An estimate is fine. You agree the final figure with the
                        client.
                      </p>
                      {pitchError && pitchError.pitchAmount && (
                        <div className={styles.errorMsg}>
                          {pitchError.pitchAmount}
                        </div>
                      )}
                      {pitchError && pitchError.pitchDeposits && (
                        <div className={styles.errorMsg}>
                          {pitchError.pitchDeposits}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {!briefRemote && (
                  <div className={styles.locationCheckboxContainer}>
                    <Checkbox
                      checked={locationCheckbox}
                      onClick={onLocationCheckboxClick}
                      className={styles.locationCheckbox}
                    >
                      <span className={styles.checkboxCopy}>
                        {`I am available to travel to ${briefLocation}`}
                      </span>
                      {pitchError && pitchError.locationCheckbox && (
                        <div className={styles.errorMsg}>
                          {pitchError.locationCheckbox}
                        </div>
                      )}
                    </Checkbox>
                    {user && user.has_resume && (
                      <Checkbox
                        checked={shareResumeCheckbox}
                        onClick={onShareResumeCheckboxClick}
                        className={styles.locationCheckbox}
                      >
                        <span className={styles.checkboxCopy}>
                          Share my resume with this client
                        </span>
                      </Checkbox>
                    )}
                  </div>
                )}
              </Fragment>
            )}

            {briefPaid && user && (
              <div className={styles.termsRow}>
                <Form>
                  <FormGroup className={styles.termsFormGroup}>
                    <Checkbox
                      className={styles.termsCheckbox}
                      onClick={() => onConfirmationCheckboxClick('terms')}
                    >
                      <span className={styles.termsText}>
                        I agree to the{' '}
                        <a href="/platform-terms" target="_blank">
                          Platform Terms
                        </a>{' '}
                        to work with this client
                      </span>
                    </Checkbox>
                  </FormGroup>
                </Form>
                {pitchError && pitchError.termsConfirmation && (
                  <div className={styles.checkboxError}>
                    <div className={styles.errorMsg}>
                      {pitchError.termsConfirmation}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className={styles.footerRow}>
              <div className={styles.footerMeta}>
                {pitchAgain ? (
                  <div className={styles.quotaStrip}>
                    <Info
                      size={16}
                      aria-hidden="true"
                      className={styles.quotaIcon}
                    />
                    <p className={styles.quotaText}>
                      No charge for this follow-up application.
                    </p>
                  </div>
                ) : willUseCredits ? (
                  <div className={styles.quotaStrip}>
                    <Info
                      size={16}
                      aria-hidden="true"
                      className={styles.quotaIcon}
                    />
                    <p className={styles.quotaText}>
                      1 application credit will be used (
                      {applicationCreditsRemaining} remaining)
                      {user.pro && !user.premium && (
                        <span className={styles.quotaUpgradeLine}>
                          <Link to="/pricing/creative">
                            Upgrade to Twine Premium
                          </Link>{' '}
                          for unlimited applications
                        </span>
                      )}
                    </p>
                  </div>
                ) : !user.pro && !user.premium ? (
                  <div className={styles.quotaStrip}>
                    <Info
                      size={16}
                      aria-hidden="true"
                      className={styles.quotaIcon}
                    />
                    <p className={styles.quotaText}>
                      <strong>
                        {introsLeft} free application
                        {introsLeft === 1 ? '' : 's'} left today.
                      </strong>{' '}
                      Resets in {dailyResetIn}.
                      <span className={styles.quotaUpgradeLine}>
                        <Link
                          to="/pricing/creative"
                          title="50 applications a month"
                        >
                          Upgrade to Pro
                        </Link>{' '}
                        for 50 a month.
                      </span>
                    </p>
                  </div>
                ) : (
                  user.pro &&
                  !user.premium && (
                    <div className={styles.quotaStrip}>
                      <Info
                        size={16}
                        aria-hidden="true"
                        className={styles.quotaIcon}
                      />
                      <p className={styles.quotaText}>
                        {introsLeft} application
                        {introsLeft === 1 ? '' : 's'} left for next{' '}
                        {pitchLimitRenewal}
                        <span className={styles.quotaUpgradeLine}>
                          <Link to="/pricing/creative">
                            Upgrade to Twine Premium
                          </Link>{' '}
                          for unlimited applications
                        </span>
                      </p>
                    </div>
                  )
                )}
              </div>

              <Button
                bsStyle="primary"
                className={styles.submitButton}
                disabled={submittingPitch}
                onClick={() => onSubmitPitch()}
              >
                {submittingPitch ? (
                  <em>
                    {showInterest ? 'Sending...' : 'Sending application...'}
                  </em>
                ) : showInterest ? (
                  <>
                    Show interest
                    <Send
                      size={16}
                      aria-hidden="true"
                      className={styles.submitIcon}
                    />
                  </>
                ) : (
                  <>
                    Send application
                    <Send
                      size={16}
                      aria-hidden="true"
                      className={styles.submitIcon}
                    />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

      {/* Preview of user card */}
      {(canPitch &&
        user &&
        (!user.bio ||
          user.bio == '' ||
          !user.top_roles ||
          (user.top_roles && user.top_roles.length == 0) ||
          !user.location ||
          (user.location && user.location.text == ''))) ||
        (pitchText !== '' && (
          <>
            <Panel
              className="f f-row ac-center jc-sb"
              style={{
                padding: '10px 20px 0px 20px',
              }}
            >
              <p className="fs-16 font-bold" style={{ margin: 0 }}>
                Application preview
              </p>
              <Link
                className="pull-right mt--"
                to={`${user.links.main_relative}`}
              >
                Edit Portfolio
              </Link>
            </Panel>

            {/* Freelancer Profile Preview */}
            <CreativePitchUserCard
              user={user}
              pitch={{ text: pitchText }}
              preferredRole={briefRole}
            />
          </>
        ))}

      <Modal
        closeButton
        show={showProModal}
        title={'Upgrade to Pro'}
        onExit={() => setShowProModal(false)}
      >
        <div className={styles.aiPitchModal}>
          <p>
            Save time with a customised application written by AI. This feature
            is only available to Twine Pro members
          </p>
          <div style={{ textAlign: 'center' }}>
            <Button href="/pricing/creative" bsStyle="primary">
              Upgrade to Pro
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

CreativePitch.propTypes = {
  availabilityToggle: PropTypes.number,
  briefCurrency: PropTypes.string,
  briefId: PropTypes.number,
  defaultInterestMessage: PropTypes.string,
  briefRole: PropTypes.string,
  briefPaid: PropTypes.bool,
  briefRemote: PropTypes.bool,
  briefLocation: PropTypes.string,
  briefNoPitchLimits: PropTypes.bool,
  creativeJob: PropTypes.bool,
  creditsRoles: PropTypes.array,
  introsLeft: PropTypes.number,
  introsLoading: PropTypes.bool,
  applicationCreditsRemaining: PropTypes.number,
  willUseCredits: PropTypes.bool,
  pitchAgain: PropTypes.bool,
  showInterest: PropTypes.bool,
  pitchText: PropTypes.string,
  pitchAmount: PropTypes.string,
  submittingPitch: PropTypes.bool,
  totalPitches: PropTypes.number,
  pitchError: PropTypes.object,
  onChangeAvailability: PropTypes.func,
  onChangeAvailabilityToggle: PropTypes.func,
  onChangePitchAmount: PropTypes.func,
  onChangePitchText: PropTypes.func,
  onGeneratePitch: PropTypes.func,
  onImportLastPitch: PropTypes.func,
  onSubmitPitch: PropTypes.func,
  loadingGeneratedPitch: PropTypes.bool,
  locationCheckbox: PropTypes.bool,
  onLocationCheckboxClick: PropTypes.func,
  shareResumeCheckbox: PropTypes.bool,
  onShareResumeCheckboxClick: PropTypes.func,
  onConfirmationCheckboxClick: PropTypes.func,
  authUserWasInvited: PropTypes.bool,
  onAttachUpload: PropTypes.func,
  jobRole: PropTypes.string,
  suggestedRoles: PropTypes.array,
  loadingSuggestedRoles: PropTypes.bool,
  user: PropTypes.object,
};

export default memo(CreativePitch);
